import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Loader2, RefreshCw, Check, MessageSquare, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import type { ManagedCampaignData } from "@/components/managed-campaign/ManagedCampaignData";
import { RegenerateFeedbackDialog } from "./RegenerateFeedbackDialog";

interface AiGenerateMessagesCardProps {
  companyProfileId: string;
  campaignKnowledgeId: string;
  onGenerated: (data: Partial<ManagedCampaignData>) => void;
  hasGenerated?: boolean;
  isPremium?: boolean;
  // Current sequences to infer previous settings from
  currentSequences?: ManagedCampaignData["sequences"];
}

export function AiGenerateMessagesCard({
  companyProfileId,
  campaignKnowledgeId,
  onGenerated,
  hasGenerated = false,
  isPremium = false,
  currentSequences = [],
}: AiGenerateMessagesCardProps) {
  // Infer initial settings from current sequences (for regeneration)
  const inferSettings = () => {
    if (currentSequences.length === 0) {
      return { withConnection: true, msgCount: 2 };
    }
    // Check if first step is a connection request
    const hasConnect = currentSequences.some(s => s.action_type === "connect");
    // Count non-end, non-connect steps as message count
    const messageSteps = currentSequences.filter(s => s.action_type === "message").length;
    return {
      withConnection: hasConnect,
      msgCount: hasConnect ? Math.max(1, messageSteps) : Math.max(1, messageSteps)
    };
  };

  const initialSettings = inferSettings();
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [generated, setGenerated] = useState(hasGenerated);
  const [messageCount, setMessageCount] = useState<number>(hasGenerated ? initialSettings.msgCount : 2);
  const [includeConnectionRequest, setIncludeConnectionRequest] = useState<boolean>(hasGenerated ? initialSettings.withConnection : true);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);

  // Sync generated state with hasGenerated prop (for reset scenarios)
  useEffect(() => {
    setGenerated(hasGenerated);
    if (!hasGenerated) {
      setExplanation(null);
    }
  }, [hasGenerated]);

  const handleGenerate = async (feedback?: string) => {
    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-generate-campaign", {
        body: {
          companyProfileId,
          campaignKnowledgeId,
          generateType: "messages",
          messageCount: messageCount,
          includeConnectionRequest: includeConnectionRequest,
          isPremium: isPremium, // Pass premium status for character limit enforcement
          feedback: feedback || undefined, // Pass feedback for regeneration
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      // Add end step to sequences if not present
      let sequences = data.sequences || [];
      if (sequences.length > 0 && sequences[sequences.length - 1]?.action_type !== "end") {
        sequences = [...sequences, {
          step_number: sequences.length + 1,
          action_type: "end" as const,
          delay_hours: 0,
        }];
      }

      onGenerated({ sequences });
      setExplanation(data.explanation);
      setGenerated(true);
      toast.success("Message sequence generated! Review and adjust below.");
    } catch (error: any) {
      console.error("Generation error:", error);
      toast.error("Failed to generate: " + (error.message || "Unknown error"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerateWithFeedback = async (feedback: string) => {
    await handleGenerate(feedback);
  };

  return (
    <>
      <Card className={cn(
        "transition-all",
        generated 
          ? "bg-emerald-500/5 border-emerald-500/20" 
          : "bg-gradient-to-br from-primary/5 via-transparent to-primary/5 border-primary/20"
      )}>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className={cn(
              "p-2.5 rounded-xl shrink-0",
              generated ? "bg-emerald-500/10" : "bg-gradient-to-br from-primary/20 to-primary/20"
            )}>
              {generated ? (
                <Check className="h-5 w-5 text-emerald-500" />
              ) : (
                <MessageSquare className="h-5 w-5 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold mb-1">
                {generated ? "Messages Generated" : "Generate with AI"}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {generated
                  ? "Your message sequence has been generated. Review and adjust below."
                  : "AI will create personalized messages based on your business and strategy profiles."}
              </p>

              {explanation && (
                <div className="bg-muted/50 rounded-lg p-2.5 mb-3">
                  <p className="text-sm text-muted-foreground">{explanation}</p>
                </div>
              )}

              {/* Connection Request Toggle - Simplified */}
              {!generated && (
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <Label className="text-sm font-medium">Type:</Label>
                  <RadioGroup
                    value={includeConnectionRequest ? "with" : "without"}
                    onValueChange={(v) => setIncludeConnectionRequest(v === "with")}
                    className="flex gap-2"
                    disabled={isGenerating}
                  >
                    <label
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-sm",
                        includeConnectionRequest
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-muted/50 border-border hover:border-primary/50"
                      )}
                    >
                      <RadioGroupItem value="with" id="with-connection" className="sr-only" />
                      <UserPlus className="h-3.5 w-3.5" />
                      <span>With Connection</span>
                    </label>
                    <label
                      className={cn(
                        "flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all text-sm",
                        !includeConnectionRequest
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-muted/50 border-border hover:border-primary/50"
                      )}
                    >
                      <RadioGroupItem value="without" id="without-connection" className="sr-only" />
                      <MessageSquare className="h-3.5 w-3.5" />
                      <span>Direct Messages</span>
                    </label>
                  </RadioGroup>
                </div>
              )}

              {/* Message Count Selector */}
              {!generated && (
                <div className="flex items-center gap-3 mb-3">
                  <label className="text-sm font-medium whitespace-nowrap">
                    {includeConnectionRequest ? "Follow-up messages:" : "Messages in sequence:"}
                  </label>
                  <Select 
                    value={messageCount.toString()} 
                    onValueChange={(v) => setMessageCount(parseInt(v))}
                    disabled={isGenerating}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1</SelectItem>
                      <SelectItem value="2">2</SelectItem>
                      <SelectItem value="3">3</SelectItem>
                      <SelectItem value="4">4</SelectItem>
                      <SelectItem value="5">5</SelectItem>
                    </SelectContent>
                  </Select>
                  {includeConnectionRequest && (
                    <span className="text-xs text-muted-foreground">
                      (+ connection request)
                    </span>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                {generated ? (
                  <Button
                    onClick={() => setShowFeedbackDialog(true)}
                    disabled={isGenerating}
                    variant="outline"
                    size="sm"
                    className="gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Regenerate
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleGenerate()}
                    disabled={isGenerating}
                    size="sm"
                    className="gap-2 bg-gradient-to-r from-primary to-primary hover:from-primary hover:to-primary"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        Generate Messages
                      </>
                    )}
                  </Button>
                )}

                {!generated && (
                  <p className="text-xs text-muted-foreground">
                    Or define manually below
                  </p>
                )}
              </div>

              {/* Variable hint */}
              {!generated && (
                <div className="mt-3 pt-3 border-t border-dashed flex items-center gap-2 text-muted-foreground text-xs">
                  <span>Available variables:</span>
                  <code className="bg-muted px-1.5 py-0.5 rounded">{"{first_name}"}</code>
                  <code className="bg-muted px-1.5 py-0.5 rounded">{"{company}"}</code>
                  <code className="bg-muted px-1.5 py-0.5 rounded">{"{title}"}</code>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <RegenerateFeedbackDialog
        open={showFeedbackDialog}
        onOpenChange={setShowFeedbackDialog}
        onRegenerate={handleRegenerateWithFeedback}
        type="messages"
        isLoading={isGenerating}
      />
    </>
  );
}
